<template>
	<div>
		<AppList :items="usersPermissions" class="users-permissions-list">
			<template #dropdown-header="{item, index}">
				<div @click="clickPermissionDropdownHeader(index)" class="dropdown-header row">
					<div class="list-item-left-side">
						<p class="list-item-title" :class="{'add-permission-p': item.user_id === 'add'}">{{ item.title }}</p>
						<div class="expired-label" v-if="expired(item.user_id)">
							<p class="expired-text">{{ getString('permissions_expired') }}</p>
						</div>
					</div>
					<AppChevronIcon :direction="item.active ? 'up' : 'down'" />
				</div>
			</template>
			<template #permission-item="{item}">
				<AppList :items="item && item.permissionItems ? item.permissionItems : []" class="permissions-list">
					<template #add-user-email>
						<div class="row input-button-row select-row">
							<p>{{ getString('email') }}</p>
							<AppTextInput
								class="add-user-input"
								ref="add-user-input"
								:placeholder="getString('email')"
							/>
						</div>
					</template>
					<template #checklist-header="{item}">
						<div class="row input-button-row select-row">
							<p>{{ item.title }}</p>
							<div class="checklist-container">
								<div class="row centered flex-end">
									<p class="mr-10 color-txt-2">{{getPermissionsCounter(item.user_id)}}</p>
									<AppButton :text="getString('edit')" outline @click="togglePermissionEditor(item)"/>
								</div>
							</div>
						</div>
					</template>
					<template #expiry-dropdown="{item}">
						<div class="row centered flex-end p-10">
							<DatePicker ref="date-picker" v-model="item.expires" mode="datetime" color="teal" :min-date='now' :is-dark="userStore.darkMode">
								<template #default="{ togglePopover }">
									<AppTextInput class="text-input ml-auto" @click="togglePopover()" readonly :placeholder="getString('expire_date')" :value="item.expires ? formatDateTimeWithSeconds(item.expires, false) : null" />
								</template>
							</DatePicker>
						</div>
					</template>
					<template #add-user-btn>
						<div class="row flex-end centered p-10">
							<AppButton @click="clickAddPermission" small :text="getString('add')" />
						</div>
					</template>
					<template #list-slot="{item}">
						<div class="row centered">
							<AppList :items="item.subitems" />
						</div>
					</template>
				</AppList>
			</template>
		</AppList>

		<AppBottomSheet @toggling-popup="togglingPopup" :active="permissionsToEditUserId !== ''" :force-width="bottomSheetWidth">
			<PermissionsEditorBottomSheet :userId="permissionsToEditUserId" @confirm="togglingPopup(false)" />
		</AppBottomSheet>
	</div>
</template>

<script>
import AppList from "@/shared/components/AppList.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppChecklist from "@/shared/components/AppChecklist.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import PermissionsEditorBottomSheet from "@/console-new/components/config/settings/general/PermissionsEditorBottomSheet.vue";

import {useUnchangingStore, useUserStore} from "@/console-new/stores";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {validateEmail} from "@/shared/helpers/strings";
import {formatDateTimeWithSeconds} from "@/shared/helpers/dates";

export default {
	name: "PermissionsEditor",
	setup() {
		let unchangingStore = useUnchangingStore();
		let userStore = useUserStore();
		let notificationStore = useNotificationStore();
		let languageStore = useLanguageStore();
		return {validateEmail, getString: languageStore.getString, userStore, unchangingStore, showNotification: notificationStore.showNotification, formatDateTimeWithSeconds};
	},
	components: {PermissionsEditorBottomSheet, AppList, AppChevronIcon, AppTextInput, AppChecklist, AppButton, AppBottomSheet},
	mixins: [WindowWidth],
	emits: ["add-user-permission", "open-add-user-permission"],
	props: {
		usersPermissions: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			permissionsToEditUserId: ''
		};
	},
	methods: {
		clickPermissionDropdownHeader(targetPermissionUserIndex) {
			let targetPermissionUser = this.usersPermissions[targetPermissionUserIndex];
			this.closeAllPermissions(targetPermissionUser)
			this.usersPermissions[targetPermissionUserIndex].active = !targetPermissionUser.active;
			if(targetPermissionUserIndex === this.usersPermissions.length - 1) {
				this.$emit('open-add-user-permission', this.usersPermissions[targetPermissionUserIndex].active);
			} else {
				this.$emit('open-add-user-permission', false);
			}
		},
		closeAllPermissions(except) {
			if(this.usersPermissions && this.usersPermissions.length) {
				this.usersPermissions.forEach(item => {
					if(item !== except) {item.active = false}
				});
			}
		},
		clickAddPermission() {
			let email = this.$refs['add-user-input'].$refs['input'].value;
			if(!this.validateEmail(email)) {
				this.showNotification('wrong-email');
				return;
			}
			let currUser = this.usersPermissions[this.usersPermissions.length - 1];
			let addingUserInfo = this.getUserPermission(currUser);
			if(!addingUserInfo.permission || !addingUserInfo.permission.length) {
				this.showNotification('no-permissions');
				return;
			}
			if(addingUserInfo.expires) { addingUserInfo.expires += " 12:00:00"; }

			addingUserInfo.email = email;
			this.$emit('add-user-permission', addingUserInfo);
		},
		getUserPermission(user) {
			let selectedPermissions = this.userStore.businessUsers[user.user_id] ? this.userStore.businessUsers[user.user_id].permission : [];

			let dateItem = user.permissionItems.find(item => item.id === 'expiration');
			let expires = dateItem.active && dateItem.expires ? formatDateTimeWithSeconds(dateItem.expires) : null;

			let stationItem = user.permissionItems.find(item => item.id === 'station');
			let requireCode = false;
			let station = false;
			if(stationItem) {
				station = stationItem.active;
				if(station) {
					let requireCodeItem = stationItem.subitems.find(item => item.id === 'require_code');
					requireCode = requireCodeItem.active;
				}
			}

			let codeItem = user.permissionItems.find(item => item.id === 'code');
			let code = null;
			if(codeItem) {
				if(codeItem && codeItem.value && codeItem.value.length > 2) {
					code = codeItem.value;
				}
			}

			return  {
				permission: selectedPermissions,
				email: user.title,
				user_id: user.user_id,
				expires: expires,
				station: station,
				require_code: requireCode,
				code: code
			};
		},
		togglingPopup(active) {
			if(!active) {
				this.permissionsToEditUserId = "";
			}
		},
		togglePermissionEditor(item) {
			this.permissionsToEditUserId = item.user_id;
		},
		getPermissionsCounter(userId) {
			if(!this.userStore.businessUsers || !this.userStore.businessUsers[userId]) {
				return "";
			}
			return this.getString('selected_permissions_counter', [this.userStore.businessUsers[userId].permission.length]);
		},
		expired(userId) {
			if (this.userStore.businessUsers && this.userStore.businessUsers[userId] && this.userStore.businessUsers[userId].expires) {
				let expiresDate = new Date(this.userStore.businessUsers[userId].expires);
				return expiresDate < new Date();
			}
			return false;
		}
	},
	computed: {
		now() {
			return new Date();
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else {
				return "960px";
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.input-button-row {
	@include centered;
	justify-content: space-between;
	padding: 6px 0;

	.input-wrapper, .select-wrapper {
		margin-right: 20px;
	}
	.add-user-input {
		margin-right: 0;
	}
	> p {
		font-size: 14px;
		max-width: calc(100% - 410px);
	}
	&.select-row > p {
		font-size: 16px;
		max-width: calc(100% - 280px);
	}
	.input-btn-container {
		min-width: 410px;
	}
	@media (max-width: 768px) {
		&:not(.select-row) {
			justify-content: flex-end;

			.row {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.input-wrapper, .select-wrapper {
				margin-right: 0;
				max-width: 100%;
				margin-bottom: 10px;
			}
			.select-wrapper{ width: 100%; }
			> p {
				max-width: 100%;
				width: 100%;
				margin-bottom: 4px;
			}
		}
	}
	@media (max-width: 600px) {
		.input-btn-container {
			min-width: unset;
			.select-wrapper{ width: auto; }
		}

		&.select-row > p {
			font-size: 14px;
		}
	}
	@media (max-width: 420px) {
		&.select-row {
			align-items: flex-start;
			.row {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.select-wrapper {
				margin-right: 0;
				margin-bottom: 10px;
			}
			> p {
				max-width: calc(100% - 180px);
				margin-bottom: 4px;
			}
		}

		.input-btn-container {
			min-width: 100%;
		}
	}
	@media (max-width: 400px) {
		&.select-row > p {
			font-size: 12px;
		}
	}
	@media (max-width: 360px) {
		&.select-row {
			justify-content: flex-end;
			.row {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.select-wrapper {
				max-width: 100%;
				width: 100%;
			}
			> p {
				max-width: 100%;
				width: 100%;
				margin-bottom: 4px;
			}
		}
	}
}
.permissions-list {
	:deep(.list-item) {
		padding: 6px 0;
	}
}
.list .list-item .users-permissions-list {
	:deep(.dropdown-content) {
		background-color: var(--color-bg-primary-2);
		box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1882352941);
		padding: 0 10px;

		.select-wrapper {
			min-width: 200px;
		}
	}
	:deep(.dropdown-header) {
		.list-item-left-side > p.list-item-title {
			@include truncateString();
			max-width: calc(100% - 40px);
		}
		.list-item-left-side > p.list-item-title.add-permission-p {
			color: $primary;
			font-weight: 500;
		}
		.list-item-left-side {
			width: calc(100% - 20px);
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: space-between;

			.expired-label {
				padding: 5px 15px;
				background-color: rgba(var(--rgb-danger), 0.2);
				border-radius: 20px;

				.expired-text {
					font-size: 14px;
				}
			}
		}
	}
}
.list .list-item .permissions-list {
	:deep(.dropdown-content) {
		background-color: transparent;
		box-shadow: none;
	}
	.input-button-row.select-row {
		> p {
			max-width: calc(100% - 260px);
		}
		.select-wrapper {
			margin-right: 0;
		}
	}
	.input-button-row.select-row .checklist-container :deep(.dropdown-content) {
		padding: 0;
	}
	@media (max-width: 600px) {
		.input-button-row.select-row {
			justify-content: flex-end;
			> p {
				margin-right: auto;
				max-width: calc(100% - 205px);
			}
			.row {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;

				.select-wrapper {
					width: 100%;
					max-width: 100%;
				}
			}
		}
	}
	@media (max-width: 420px) {
		.input-button-row.select-row {
			> p {
				max-width: 100%;
				width: 100%;
				margin-bottom: 10px;
			}
			.checklist-container {
				max-width: 100%;
				width: 100%;
			}
			.row .select-wrapper {
				min-width: 100%;
				max-width: 100%;
				width: 100%;
			}
		}
	}
}
.permission-list-header {
	@include centered;
	padding: 5px 0;
	justify-content: space-between;
	cursor: pointer;

	* {
		pointer-events: none;
	}

	.checkbox-container {
		width: 30px;
		height: 25px;
	}
}
</style>